import React, { useContext } from "react";
import { Avatar, Card, CardActions, CardContent, CardHeader, CardMedia, Divider, IconButton, List, ListItem, Menu, MenuItem, Typography } from "@mui/material";
import { format } from "date-fns";
import Chip from "@mui/material/Chip";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupsIcon from '@mui/icons-material/Groups';

import { AppContext } from "../Store";
import { Link } from "react-router-dom";
import { useSnackbar } from "../shared/SnackbarProvider";
import { Repo } from "../Repo";

const ObservationCard: React.FC<any> = ({ observation }) => {

  const context = useContext(AppContext);
  const { userContext } = context;

  const snack = useSnackbar();
  const repo = new Repo();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const markReviewed = async () => {
    setAnchorEl(null);

    try {
      await repo.markObservationReviewed(observation);
      snack("'Reviewed' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as reviewed.", "error");
    }

  };

  const markVisibleToParents = async () => {
    setAnchorEl(null);

    try {
      await repo.markObservationVisibleToParents(observation);
      snack("'Visible to Parents' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as reviewed.", "error");
    }
  };

  const deleteObservation = async () => {
    setAnchorEl(null);

    try {
      await repo.deleteObservation(observation);
      snack("'Deleted' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as deleted.", "error");
    }
  };

  return (
    <Card variant="outlined" sx={{ maxWidth: 600, margin: "auto" }}>
      <CardHeader
        action={
          userContext.isTeacher() &&
          <IconButton aria-label="settings" onClick={handleClick} >
            <MoreVertIcon />
          </IconButton>
        }
        title={<>
          {observation.category}
          {observation.childId.includes(",") && <GroupsIcon sx={{ marginLeft: 3 }} />}
        </>}
        subheader={format(new Date(observation.observationDate), "yyyy-MMM-dd")}
      />
      {
        userContext.isTeacher() &&
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {userContext.isAdmin() && <MenuItem onClick={markReviewed}>Reviewed</MenuItem>}
          {userContext.isAdmin() && <MenuItem onClick={markVisibleToParents}>Visible to Parents</MenuItem>}
          {userContext.isAdmin() && <MenuItem onClick={deleteObservation}>Delete</MenuItem>}

          <MenuItem component={Link} to={observation.editUrl} target="_blank">Edit</MenuItem>
          {observation.childId.includes(",") && <Divider />}
          {
            observation.childId.includes(",") &&
            observation.childId.split(",").map((x: string) => x.trim()).map((childId: string) => {
              return (
                <MenuItem disabled
                  key={childId}
                  id={childId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Chip
                    label={childId}
                    avatar={<Avatar>{childId.charAt(0).toUpperCase()}</Avatar>}
                    component="span"
                    size="small"
                  />
                </MenuItem>
              )
            })
          }
        </Menu>
      }
      {observation.photoUrl && <CardMedia
        component="img"
        sx={{
          objectFit: "contain",
          width: "100%",
          height: "auto",
        }}
        image={observation.photoUrl} />}
      <CardContent>
        <Typography color="text.secondary"><b>Familiarity</b>: {observation.familiarity}</Typography>
        <Typography color="text.secondary"><b>Concentration</b>: {observation.concentration}</Typography>
        <Typography color="text.secondary"><b>Notes</b>: {observation.observationNotes}</Typography>
        {
          userContext.isTeacher() && (
            <Typography className="no-print" color="text.secondary"><b>Next steps</b>: {observation.nextSteps}</Typography>
          )
        }
      </CardContent>
      {
        userContext.isTeacher() &&
        <CardActions className="no-print">
          <Chip
            label="Reviewed"
            size="small"
            variant="outlined"
            color={observation.reviewed ? "primary" : "error"}
            icon={observation.reviewed ? <CheckCircleIcon /> : <CancelIcon />}
          />
          <Chip
            label="Visible to parents"
            size="small"
            variant="outlined"
            color={observation.visibleToParents ? "primary" : "error"}
            icon={observation.visibleToParents ? <CheckCircleIcon /> : <CancelIcon />}
          />
        </CardActions>
      }
    </Card>
  );
};

export default ObservationCard;
